/**
 * ReactionStepEditor Component
 * 
 * Phase 5: CRUD UI for managing reaction steps
 * 
 * Features:
 * - List/table of existing steps
 * - Add new step (modal/form)
 * - Edit existing step
 * - Delete step
 * - Link steps to materials from BOM
 * - Validation (require at least 1 reactant and 1 product)
 */

import React, { useState } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle } from 'lucide-react';
import { ChemExperimentState, ReactionStep, ReactionType, MaterialLine } from './types';
import { addReactionStep, updateReactionStep, deleteReactionStep, generateStepId } from './chemExperimentHelpers';

interface ReactionStepEditorProps {
  experiment: ChemExperimentState;
  onChange: (updated: ChemExperimentState) => void;
}

const REACTION_TYPES: ReactionType[] = [
  'Peptide',
  'Enzyme',
  'FlowChemistry',
  'Lyophilisation',
  'Chromatography',
  'PhiTec',
  'Polymorphism',
  'Premix',
  'SprayDrying',
  'TSU',
];

/**
 * ReactionStepEditor Component
 */
export const ReactionStepEditor: React.FC<ReactionStepEditorProps> = ({
  experiment,
  onChange,
}) => {
  const [editingStep, setEditingStep] = useState<ReactionStep | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<ReactionStep>>({
    name: '',
    type: 'Peptide',
    reactantMaterialIds: [],
    productMaterialIds: [],
    conditions: {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const steps = experiment.steps || [];
  const materials = experiment.materials || [];

  /**
   * Open modal to add new step
   */
  const handleAddStep = () => {
    setEditingStep(null);
    setFormData({
      name: '',
      type: 'Peptide',
      reactantMaterialIds: [],
      productMaterialIds: [],
      conditions: {},
    });
    setErrors({});
    setIsModalOpen(true);
  };

  /**
   * Open modal to edit existing step
   */
  const handleEditStep = (step: ReactionStep) => {
    setEditingStep(step);
    setFormData({
      name: step.name,
      type: step.type,
      reactantMaterialIds: [...step.reactantMaterialIds],
      productMaterialIds: [...step.productMaterialIds],
      conditions: { ...step.conditions },
    });
    setErrors({});
    setIsModalOpen(true);
  };

  /**
   * Delete a step
   */
  const handleDeleteStep = (stepId: string) => {
    if (window.confirm('Are you sure you want to delete this reaction step?')) {
      const updated = deleteReactionStep(experiment, stepId);
      onChange(updated);
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Step name is required';
    }

    if (!formData.reactantMaterialIds || formData.reactantMaterialIds.length === 0) {
      newErrors.reactants = 'At least one reactant is required';
    }

    if (!formData.productMaterialIds || formData.productMaterialIds.length === 0) {
      newErrors.products = 'At least one product is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Save step (add or update)
   */
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const stepData: ReactionStep = {
      id: editingStep?.id || generateStepId(),
      name: formData.name!,
      type: formData.type!,
      reactantMaterialIds: formData.reactantMaterialIds || [],
      productMaterialIds: formData.productMaterialIds || [],
      conditions: formData.conditions || {},
    };

    let updated: ChemExperimentState;
    if (editingStep) {
      updated = updateReactionStep(experiment, stepData);
    } else {
      updated = addReactionStep(experiment, stepData);
    }

    onChange(updated);
    setIsModalOpen(false);
    setEditingStep(null);
  };

  /**
   * Toggle material selection in multi-select
   */
  const toggleMaterialSelection = (
    materialId: string,
    field: 'reactantMaterialIds' | 'productMaterialIds'
  ) => {
    const currentIds = formData[field] || [];
    const index = currentIds.indexOf(materialId);
    
    if (index >= 0) {
      // Remove
      setFormData({
        ...formData,
        [field]: currentIds.filter((id) => id !== materialId),
      });
    } else {
      // Add
      setFormData({
        ...formData,
        [field]: [...currentIds, materialId],
      });
    }
  };

  /**
   * Check if a material has a structure defined
   */
  const hasStructure = (materialId: string): boolean => {
    const material = materials.find((m) => m.id === materialId);
    return !!(material?.structure?.smiles || material?.structure?.molfile);
  };

  /**
   * Get material display label
   */
  const getMaterialLabel = (material: MaterialLine): string => {
    return `${material.name} (${material.role})`;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>Reaction Steps</h3>
        <button style={styles.addButton} onClick={handleAddStep}>
          <Plus size={16} />
          Add Step
        </button>
      </div>

      {/* Steps List */}
      {steps.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No reaction steps defined</p>
          <p style={styles.emptySubtext}>
            Click "Add Step" to create your first reaction step
          </p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Step Name</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Reactants</th>
                <th style={styles.th}>Products</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step) => {
                const reactantNames = step.reactantMaterialIds
                  .map((id) => materials.find((m) => m.id === id)?.name)
                  .filter(Boolean)
                  .join(', ') || 'None';
                
                const productNames = step.productMaterialIds
                  .map((id) => materials.find((m) => m.id === id)?.name)
                  .filter(Boolean)
                  .join(', ') || 'None';

                const missingStructures = [
                  ...step.reactantMaterialIds,
                  ...step.productMaterialIds,
                ].filter((id) => !hasStructure(id));

                return (
                  <tr key={step.id} style={styles.tr}>
                    <td style={styles.td}>{step.name}</td>
                    <td style={styles.td}>{step.type}</td>
                    <td style={styles.td}>
                      <div style={styles.materialList}>
                        {reactantNames}
                        {step.reactantMaterialIds.some((id) => !hasStructure(id)) && (
                          <span style={styles.warningBadge} title="Missing structure">
                            <AlertCircle size={12} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.materialList}>
                        {productNames}
                        {step.productMaterialIds.some((id) => !hasStructure(id)) && (
                          <span style={styles.warningBadge} title="Missing structure">
                            <AlertCircle size={12} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          style={styles.iconButton}
                          onClick={() => handleEditStep(step)}
                          title="Edit step"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          style={styles.iconButton}
                          onClick={() => handleDeleteStep(step.id)}
                          title="Delete step"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {editingStep ? 'Edit Reaction Step' : 'Add Reaction Step'}
              </h3>
              <button
                style={styles.modalCloseButton}
                onClick={() => setIsModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Step Name */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Step Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  style={{
                    ...styles.input,
                    ...(errors.name ? styles.inputError : {}),
                  }}
                  value={formData.name || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Peptide Coupling"
                />
                {errors.name && (
                  <span style={styles.errorText}>{errors.name}</span>
                )}
              </div>

              {/* Step Type */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Reaction Type <span style={styles.required}>*</span>
                </label>
                <select
                  style={styles.select}
                  value={formData.type || 'Peptide'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value as ReactionType,
                    })
                  }
                >
                  {REACTION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reactants */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Reactants <span style={styles.required}>*</span>
                </label>
                {materials.length === 0 ? (
                  <div style={styles.noMaterials}>
                    No materials available. Add materials in the BOM tab first.
                  </div>
                ) : (
                  <div style={styles.multiSelect}>
                    {materials.map((material) => {
                      const isSelected =
                        formData.reactantMaterialIds?.includes(material.id) ||
                        false;
                      return (
                        <label
                          key={material.id}
                          style={{
                            ...styles.checkboxLabel,
                            ...(isSelected ? styles.checkboxLabelSelected : {}),
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              toggleMaterialSelection(
                                material.id,
                                'reactantMaterialIds'
                              )
                            }
                            style={styles.checkbox}
                          />
                          <span>{getMaterialLabel(material)}</span>
                          {!hasStructure(material.id) && (
                            <span style={styles.warningBadge} title="Missing structure">
                              <AlertCircle size={12} />
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
                {errors.reactants && (
                  <span style={styles.errorText}>{errors.reactants}</span>
                )}
              </div>

              {/* Products */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Products <span style={styles.required}>*</span>
                </label>
                {materials.length === 0 ? (
                  <div style={styles.noMaterials}>
                    No materials available. Add materials in the BOM tab first.
                  </div>
                ) : (
                  <div style={styles.multiSelect}>
                    {materials.map((material) => {
                      const isSelected =
                        formData.productMaterialIds?.includes(material.id) ||
                        false;
                      return (
                        <label
                          key={material.id}
                          style={{
                            ...styles.checkboxLabel,
                            ...(isSelected ? styles.checkboxLabelSelected : {}),
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              toggleMaterialSelection(
                                material.id,
                                'productMaterialIds'
                              )
                            }
                            style={styles.checkbox}
                          />
                          <span>{getMaterialLabel(material)}</span>
                          {!hasStructure(material.id) && (
                            <span style={styles.warningBadge} title="Missing structure">
                              <AlertCircle size={12} />
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
                {errors.products && (
                  <span style={styles.errorText}>{errors.products}</span>
                )}
              </div>

              {/* Conditions */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Conditions (Optional)</label>
                <div style={styles.conditionsGrid}>
                  <div>
                    <label style={styles.sublabel}>Temperature</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={formData.conditions?.temperature || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          conditions: {
                            ...formData.conditions,
                            temperature: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., 25°C"
                    />
                  </div>
                  <div>
                    <label style={styles.sublabel}>Pressure</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={formData.conditions?.pressure || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          conditions: {
                            ...formData.conditions,
                            pressure: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., 1 atm"
                    />
                  </div>
                  <div>
                    <label style={styles.sublabel}>Time</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={formData.conditions?.time || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          conditions: {
                            ...formData.conditions,
                            time: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., 2 h"
                    />
                  </div>
                  <div>
                    <label style={styles.sublabel}>pH</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={formData.conditions?.pH || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          conditions: {
                            ...formData.conditions,
                            pH: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., 7.0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button
                style={styles.cancelButton}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button style={styles.saveButton} onClick={handleSave}>
                {editingStep ? 'Update' : 'Add'} Step
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: 500,
    color: '#64748b',
  },
  emptySubtext: {
    margin: 0,
    fontSize: '14px',
    color: '#94a3b8',
  },
  tableContainer: {
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    fontWeight: 600,
    color: '#475569',
    fontSize: '12px',
  },
  tr: {
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '12px',
    color: '#1e293b',
  },
  materialList: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  warningBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#f59e0b',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  iconButton: {
    padding: '6px',
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flexGrow: 1,
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#475569',
  },
  sublabel: {
    display: 'block',
    marginBottom: '4px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#1e293b',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#1e293b',
    backgroundColor: '#fff',
  },
  multiSelect: {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px',
    maxHeight: '200px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1e293b',
  },
  checkboxLabelSelected: {
    backgroundColor: '#eff6ff',
    color: '#2563eb',
  },
  checkbox: {
    cursor: 'pointer',
  },
  noMaterials: {
    padding: '16px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '13px',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
  },
  conditionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  errorText: {
    display: 'block',
    marginTop: '4px',
    fontSize: '12px',
    color: '#ef4444',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    cursor: 'pointer',
  },
};

